import { useState, useEffect, useCallback } from 'react';
import { collection, onSnapshot, doc, setDoc, updateDoc, query, orderBy } from 'firebase/firestore';
import { db, addAuditLog } from '../../firebase';
import { useAuth } from '../../context/AuthContext';
import PageHeader from '../../components/PageHeader';
import ProductModal from '../../components/ProductModal';
import ToastNotification from '../../components/ToastNotification';
import styles from './SupplierInventory.module.css';

function stockStatus(stock) {
  if (stock >= 20) return 'In Stock';
  if (stock >= 10) return 'Low Stock';
  return 'Critical';
}

function StatusBadge({ stock }) {
  const status = stockStatus(stock);
  const cls = { 'In Stock': styles.badgeGreen, 'Low Stock': styles.badgeYellow, 'Critical': styles.badgeRed }[status];
  return <span className={`${styles.badge} ${cls}`}>{status}</span>;
}


export default function SupplierInventory() {
  const { user }                            = useAuth();
  const [inventory, setInventory]           = useState([]);
  const [search, setSearch]                 = useState('');
  const [categoryFilter, setCategoryFilter] = useState('All');
  const [modal, setModal]                   = useState(null);
  const [toast, setToast]                   = useState(null);
  const [deleteTarget, setDeleteTarget]     = useState(null);
  const dismissToast = useCallback(() => setToast(null), []);

  useEffect(() => {
    const q = query(collection(db, 'products'), orderBy('name'));
    const unsub = onSnapshot(q, snap => {
      setInventory(snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(p => !p.isArchived));
    });
    return unsub;
  }, []);

  const categories = ['All', ...Array.from(new Set(inventory.map(p => p.category).filter(Boolean))).sort()];

  const filtered = inventory
    .filter(p => {
      const matchesSearch = !search || p.name.toLowerCase().includes(search.toLowerCase()) || p.id.toLowerCase().includes(search.toLowerCase());
      const matchesCategory = categoryFilter === 'All' || p.category === categoryFilter;
      return matchesSearch && matchesCategory;
    })
    .sort((a, b) => {
      const catCmp = (a.category ?? '').localeCompare(b.category ?? '');
      return catCmp !== 0 ? catCmp : a.name.localeCompare(b.name);
    });

  async function handleAdd(data) {
    try {
      const { id, ...rest } = data;
      await setDoc(doc(db, 'products', id), { ...rest, isArchived: false });
      await addAuditLog(user.uid, user.name, `Added product ${id}: ${data.name}`, 'Inventory');
      setModal(null);
      setToast({ message: 'Product added successfully.', type: 'success' });
    } catch (e) {
      console.error('Add product failed:', e);
      setToast({ message: 'Failed to add product.', type: 'error' });
    }
  }

  async function handleEdit(data) {
    try {
      const { id, ...rest } = data;
      await updateDoc(doc(db, 'products', id), rest);
      await addAuditLog(user.uid, user.name, `Updated product ${id}: ${data.name}`, 'Inventory');
      setModal(null);
      setToast({ message: 'Product updated.', type: 'success' });
    } catch (e) {
      console.error('Edit product failed:', e);
      setToast({ message: 'Failed to update product.', type: 'error' });
    }
  }

  async function handleDelete(id) {
    try {
      await updateDoc(doc(db, 'products', id), { isArchived: true });
      await addAuditLog(user.uid, user.name, `Archived product ${id}`, 'Inventory');
      setDeleteTarget(null);
      setToast({ message: 'Product archived.', type: 'success' });
    } catch (e) {
      console.error('Archive product failed:', e);
      setToast({ message: 'Failed to archive product.', type: 'error' });
    }
  }

  return (
    <div className={styles.page}>
      <PageHeader title="Inventory Management" description="Manage stock levels and product pricing" />

      <div className={styles.toolbar}>
        <div className={styles.searchWrap}>
          <svg className={styles.searchIcon} viewBox="0 0 20 20" fill="none">
            <circle cx="9" cy="9" r="6" stroke="currentColor" strokeWidth="1.8"/>
            <path d="M13.5 13.5L17 17" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
          </svg>
          <input className={styles.searchInput} type="text" placeholder="Search by product name or ID…" value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <button className={styles.addBtn} onClick={() => setModal('add')}>+ Add Product</button>
      </div>

      <div className={styles.categoryFilters}>
        {categories.map(cat => (
          <button
            key={cat}
            className={`${styles.categoryPill} ${categoryFilter === cat ? styles.categoryPillActive : ''}`}
            onClick={() => setCategoryFilter(cat)}
          >
            {cat}
          </button>
        ))}
      </div>

      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Image</th><th>Product ID</th><th>Product Name</th>
              <th>Category</th><th>Unit</th><th>Stock</th><th>Price</th><th>Status</th><th>Action</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={9} className={styles.empty}>{inventory.length === 0 ? 'Loading inventory…' : 'No products match your search.'}</td></tr>
            ) : (
              filtered.map((product, idx) => (
                <tr key={product.id} className={idx % 2 === 0 ? styles.rowEven : styles.rowOdd}>
                  <td><img
                    src={`/${product.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')}.png`}
                    alt={product.name}
                    className={styles.imgPlaceholder}
                    style={{ objectFit: 'contain', background: 'transparent', display: 'block' }}
                  /></td>
                  <td className={styles.productId}>{product.id}</td>
                  <td className={styles.productName}>{product.name}</td>
                  <td>{product.category}</td>
                  <td>{product.unit ?? '—'}</td>
                  <td className={styles.stockNum}>{product.stock}</td>
                  <td className={styles.price}>₱{product.price.toLocaleString()}</td>
                  <td><StatusBadge stock={product.stock} /></td>
                  <td>
                    <div className={styles.actionGroup}>
                      <button className={styles.editBtn} onClick={() => setModal({ mode: 'edit', product })}>Edit</button>
                      <button className={styles.deleteBtn} onClick={() => setDeleteTarget(product.id)}>Delete</button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {modal === 'add' && <ProductModal mode="add" onSave={handleAdd} onCancel={() => setModal(null)} />}
      {modal?.mode === 'edit' && <ProductModal mode="edit" initialData={modal.product} onSave={handleEdit} onCancel={() => setModal(null)} />}

      {deleteTarget && (
        <div className={styles.overlay} onClick={() => setDeleteTarget(null)}>
          <div className={styles.confirmModal} onClick={e => e.stopPropagation()}>
            <h3 className={styles.confirmTitle}>Archive Product</h3>
            <p className={styles.confirmText}>
              Are you sure you want to archive <strong>{deleteTarget}</strong>? It will be hidden from inventory but not permanently deleted.
            </p>
            <div className={styles.confirmActions}>
              <button className={styles.cancelBtn} onClick={() => setDeleteTarget(null)}>Cancel</button>
              <button className={styles.confirmDeleteBtn} onClick={() => handleDelete(deleteTarget)}>Archive</button>
            </div>
          </div>
        </div>
      )}

      {toast && <ToastNotification message={toast.message} type={toast.type} onDismiss={dismissToast} />}
    </div>
  );
}
